import { useSecureFederatedMutation } from '@at/sauron-platform-graphql'
import { ADD_SAVED_SEARCH } from '../../patterns/saved-searches/saved-searches.fed.query'
import { Alert } from '../../__generated__/fed-graphql-types'
import {
    AddSavedSearchMutation_Mutation,
    AddSavedSearchMutationVariables,
} from '../../patterns/saved-searches/__generated__/saved-searches.fed.query'
import { useUserState, SignInMode } from '@at/connected-signin'
import { useModalLoginContext } from '../../contexts/modal-login-context'
import { ApolloError } from '@apollo/client'

const saveSearchErrors = new Map<string, string>([
    ['Duplicate', 'You have already saved this search. Please create a different search and try again.'],
    ['MaxReached', 'You have reached the maximum limit for saved searches.'],
    ['unknown', 'Sorry, something went wrong. Please try again later'],
])

const getErrorMessage = (errorCode = 'unknown'): string => {
    let message = saveSearchErrors.get(errorCode)
    if (!message) {
        message = saveSearchErrors.get('unknown') as string
    }
    return message
}

class SavedSearchError extends Error {
    private code: string

    public constructor(code: string, message: string) {
        super(message)
        this.code = code
    }
}

interface SaveSearch {
    saveSearchOrSignIn: (name: string, searchUrl: string, alert: Alert) => Promise<void>
    error: ApolloError | undefined
}
export const useSavedSearch = (): SaveSearch => {
    const { isShowingModalLogin, handleToggleModalLogin } = useModalLoginContext()
    const [userState, updateUser] = useUserState()
    const [saveSearchMutation, { error }] = useSecureFederatedMutation<
        AddSavedSearchMutation_Mutation,
        AddSavedSearchMutationVariables
    >(ADD_SAVED_SEARCH, {
        fetchPolicy: 'no-cache',
    })

    const doSave = async (name: string, searchUrl: string, alert: Alert) => {
        try {
            return await saveSearchMutation({
                variables: {
                    savedSearchInput: {
                        alert,
                        name,
                        searchUrl,
                    },
                },
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (errors: any) {
            if (errors && errors?.graphQLErrors?.length > 0) {
                if (errors.graphQLErrors[0]?.extensions?.code === '401') {
                    updateUser({ ...userState, userSignedIn: false })
                    handleToggleModalLogin({
                        showing: true,
                        signInMode: SignInMode.SavedAdvert,
                        afterSignInCallback: async () => await doSave(name, searchUrl, alert),
                    })
                }
                if (errors.graphQLErrors[0]?.extensions?.code) {
                    throw new SavedSearchError(
                        errors.graphQLErrors[0]?.extensions?.code,
                        getErrorMessage(errors.graphQLErrors[0]?.extensions?.code)
                    )
                }
            }
            throw new SavedSearchError('unknown', getErrorMessage('unknown'))
        }
    }
    const saveSearchOrSignIn = async (name: string, searchUrl: string, alert: Alert) => {
        if (!userState.userSignedIn) {
            /** User not logged in, display modal */
            if (!isShowingModalLogin) {
                handleToggleModalLogin({
                    showing: true,
                    signInMode: SignInMode.SavedAdvert,
                    afterSignInCallback: async () => await doSave(name, searchUrl, alert),
                })
            }
            return
        }
        await doSave(name, searchUrl, alert)
    }
    return { error, saveSearchOrSignIn: saveSearchOrSignIn }
}
